import api from '../../api/client';
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Row, Col, Form, Select, DatePicker, Space, Card, Spin, message, Typography } from 'antd';
import { ReloadOutlined, ClearOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import config from '../../config';
import { formatKES } from '../../utils/currency';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AccountingReports = ({ reportType }) => {
    const today = dayjs();
    const defaultStartDate = today.subtract(3, 'month').format('YYYY-MM-DD');
    const [data, setData] = useState([]);
    const [trialdata, setTrialData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [filters, setFilters] = useState({
        startDate: defaultStartDate,
        endDate: today.format('YYYY-MM-DD'),
        account: '',
        limit: 1000,
        farmId: ''
    });
    const [accounts, setAccounts] = useState([]);
    const [farms, setFarms] = useState([]);
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;
    const [reportBasis, setReportBasis] = useState('accrual');

    const fetchAccounts = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/chart-of-accounts`);
            setAccounts(data);
        } catch (error) {
            message.error('Failed to fetch accounts');
        }
    }, [apiUrl]);

    const fetchFarms = useCallback(async () => {
        try {
            const { data } = await api.get(`/farm`);
            setFarms(data);
        } catch (error) {
            message.error('Failed to fetch farms');
        }
    }, [apiUrl]);

    const fetchData = useCallback(async () => {
        if (!reportType) return;
        setLoading(true);
        try {
            const params = {
                ...filters,
                basis: reportBasis,
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined
            };
            const { data } = await api.get(`/api/reports/${reportType}`, { params });
            setData(data);
            setTrialData(data);
        } catch (error) {
            message.error('Failed to fetch report data');
        } finally {
            setLoading(false);
        }
    }, [filters, reportType, reportBasis, apiUrl]);

    useEffect(() => {
        fetchAccounts();
        fetchFarms();
        fetchData();
    }, [fetchAccounts, fetchFarms, fetchData]);

    const handleClearFilters = () => {
        setFilters({
            startDate: defaultStartDate,
            endDate: dayjs().format('YYYY-MM-DD'),
            account: '',
            limit: 1000,
            farmId: ''
        });
        setReportBasis('accrual');
    };

    // ---------- TRIAL BALANCE (enhanced grouping) ----------
    const transformFinancialData = (rawData) => {
        const result = [];

        const addCategory = (name, items) => {
            if (!items || items.length === 0) return;
            const totalDebit = items.reduce((sum, item) => sum + (item.netDebit || 0), 0);
            const totalCredit = items.reduce((sum, item) => sum + (item.netCredit || 0), 0);
            const nonZeroItems = items.filter((item) => item.netDebit !== 0 || item.netCredit !== 0);
            if (nonZeroItems.length === 0 && totalDebit === 0 && totalCredit === 0) return;

            result.push({ account: name, netDebit: '', netCredit: '', isCategory: true });
            nonZeroItems.forEach((item) => {
                result.push({ ...item, account: `  ${item.account}`, isCategory: false });
            });
            result.push({ account: `${name} Subtotal`, netDebit: totalDebit, netCredit: totalCredit, isSubtotal: true });
        };

        // Order: Assets → Liabilities → Expenses (and Revenue if present)
        addCategory('ASSETS', rawData?.Assets);
        addCategory('LIABILITIES', rawData?.Liabilities);
        addCategory('EXPENSES', rawData?.Expenses);
        if (rawData?.Revenue && rawData.Revenue.length) addCategory('REVENUE', rawData.Revenue);

        // Grand total
        if (rawData?.Total && (rawData.Total.netDebit !== 0 || rawData.Total.netCredit !== 0)) {
            result.push({
                account: 'GRAND TOTAL',
                netDebit: rawData.Total.netDebit,
                netCredit: rawData.Total.netCredit,
                isTotal: true
            });
        }
        return result;
    };

    // ---------- BALANCE SHEET (hierarchical with sections, subtotals, totals) ----------
    const groupBalanceSheetData = (rawData) => {
        const sections = [
            { name: 'ASSETS', categories: ['CURRENT_ASSETS', 'LONG_TERM_ASSETS', 'OTHER_ASSETS'] },
            { name: 'LIABILITIES', categories: ['CURRENT_LIABILITIES', 'LONG_TERM_LIABILITIES'] },
            { name: 'EQUITY', categories: ['EQUITY'] }
        ];
        const rows = [];

        sections.forEach((section) => {
            let sectionTotal = 0;
            const sectionRows = [];

            section.categories.forEach((cat) => {
                const items = rawData?.[cat];
                if (items && items.length) {
                    // Filter non‑zero items first
                    const nonZeroItems = items.filter((item) => (item.balance ?? 0) !== 0);
                    if (nonZeroItems.length === 0) return;

                    const catTotal = nonZeroItems.reduce((sum, item) => sum + (item.balance ?? 0), 0);
                    if (catTotal !== 0) {
                        sectionRows.push({
                            rowType: 'subcategoryHeader',
                            account_name: cat.replace(/_/g, ' '),
                            balance: catTotal,
                            isSubtotal: true
                        });
                        sectionTotal += catTotal;
                    }
                    nonZeroItems.forEach((item) => {
                        sectionRows.push({
                            rowType: 'account',
                            account_name: item.account_name,
                            gl_code: item.gl_code,
                            balance: item.balance
                        });
                    });
                }
            });

            if (sectionRows.length > 0) {
                rows.push({ rowType: 'sectionHeader', account_name: section.name });
                rows.push(...sectionRows);
                rows.push({ rowType: 'sectionTotal', account_name: `Total ${section.name}`, balance: sectionTotal });
                rows.push({ rowType: 'spacer', account_name: '' });
            }
        });
        // Remove the last spacer if present
        if (rows.length && rows[rows.length - 1].rowType === 'spacer') rows.pop();
        return rows;
    };

    const getFilteredData = () => {
        if (reportType === 'trial-balance') {
            const transformed = transformFinancialData(trialdata);
            // Remove rows where both debit and credit are zero (except headers/subtotals/totals)
            return transformed.filter((row) => {
                if (row.isCategory || row.isSubtotal || row.isTotal) return true;
                return row.netDebit !== 0 || row.netCredit !== 0;
            });
        } else if (reportType === 'balance-sheet') {
            const grouped = groupBalanceSheetData(data);
            // Already filtered zero accounts inside grouping

            return grouped;
        } else {
            // General ledger
            return data.filter(
                (item) =>
                    (item.total_debit !== 0 && item.total_debit !== undefined) ||
                    (item.total_credit !== 0 && item.total_credit !== undefined) ||
                    (item.balance !== 0 && item.balance !== undefined)
            );
        }
    };

    const filteredData = getFilteredData();

    // ---------- EXPORT (unchanged but uses filteredData) ----------
    const exportToCSV = () => {
        const exportData = filteredData;
        if (exportData.length === 0) {
            message.warning('No data to export');
            return;
        }
        setExporting(true);
        try {
            let headers, rows;
            if (reportType === 'trial-balance') {
                headers = ['Account', 'Net Debit', 'Net Credit'];
                rows = exportData.map((item) => [item.account, item.netDebit, item.netCredit]);
            } else if (reportType === 'balance-sheet') {
                headers = ['Account Name', 'Account Code', 'Balance (KES)'];
                rows = exportData.map((item) => [item.account_name, item.gl_code || '', item.balance]);
            } else {
                headers = ['Acc Name', 'GL Code', 'Debit', 'Credit', 'Balance'];
                rows = exportData.map((item) => [item.account_name, item.account_code, item.total_debit, item.total_credit, item.balance]);
            }
            const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
            saveAs(blob, `${reportType}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
            message.success('CSV exported');
        } catch (error) {
            message.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    const exportToPDF = () => {
        const exportData = filteredData;
        if (exportData.length === 0) {
            message.warning('No data to export');
            return;
        }
        setExporting(true);
        try {
            const doc = new jsPDF();
            let head, body;
            if (reportType === 'trial-balance') {
                head = [['Account', 'Net Debit', 'Net Credit']];
                body = exportData.map((item) => [item.account, item.netDebit, item.netCredit]);
            } else if (reportType === 'balance-sheet') {
                head = [['Account Name', 'Account Code', 'Balance (KES)']];
                body = exportData.map((item) => [item.account_name, item.gl_code || '', item.balance]);
            } else {
                head = [['Acc Name', 'GL Code', 'Debit', 'Credit', 'Balance']];
                body = exportData.map((item) => [item.account_name, item.account_code, item.total_debit, item.total_credit, item.balance]);
            }
            doc.autoTable({ head, body });
            doc.save(`${reportType}_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
            message.success('PDF exported');
        } catch (error) {
            message.error('PDF export failed');
        } finally {
            setExporting(false);
        }
    };

    // ---------- COLUMN DEFINITIONS (enhanced for balance sheet hierarchy) ----------
    const getColumns = () => {
        if (!reportType) return [];

        switch (reportType) {
            case 'general-ledger':
                return [
                    {
                        title: 'Account',
                        dataIndex: 'account_name',
                        key: 'account_name',
                        sorter: (a, b) => a.account_name.localeCompare(b.account_name)
                    },
                    { title: 'Account Code', dataIndex: 'account_code', key: 'account_code', sorter: true },
                    {
                        title: 'Debit',
                        dataIndex: 'total_debit',
                        key: 'debit',
                        render: (_, record) => (
                            <Link to={`/reports/account-statement/${record.account_code}/${record.account_name}`}>
                                {formatKES(record.total_debit)}
                            </Link>
                        ),
                        align: 'right',
                        sorter: (a, b) => a.total_debit - b.total_debit
                    },
                    {
                        title: 'Credit',
                        dataIndex: 'total_credit',
                        key: 'credit',
                        render: (_, record) => (
                            <Link to={`/reports/account-statement/${record.account_code}/${record.account_name}`}>
                                {formatKES(record.total_credit)}
                            </Link>
                        ),
                        align: 'right',
                        sorter: (a, b) => a.total_credit - b.total_credit
                    },
                    {
                        title: 'Balance',
                        dataIndex: 'balance',
                        key: 'balance',
                        render: (_, record) => (
                            <Link to={`/reports/account-statement/${record.account_code}/${record.account_name}`}>
                                {formatKES(record.balance)}
                            </Link>
                        ),
                        align: 'right',
                        sorter: (a, b) => a.balance - b.balance
                    }
                ];

            case 'balance-sheet':
                return [
                    {
                        title: 'Account',
                        dataIndex: 'account_name',
                        key: 'account',
                        render: (text, record) => {
                            if (record.rowType === 'sectionHeader') return <strong style={{ fontSize: '16px' }}>{text}</strong>;
                            if (record.rowType === 'subcategoryHeader')
                                return <span style={{ paddingLeft: '20px', fontWeight: 'bold' }}>{text}</span>;
                            if (record.rowType === 'sectionTotal')
                                return <strong style={{ borderTop: '1px solid #ddd', paddingTop: '5px' }}>{text}</strong>;
                            if (record.rowType === 'account') return <span style={{ paddingLeft: '40px' }}>{text}</span>;
                            return text;
                        }
                    },
                    {
                        title: 'Code',
                        dataIndex: 'gl_code',
                        key: 'code',
                        render: (text, record) => (record.rowType === 'account' ? text : '')
                    },
                    {
                        title: 'Balance (KES)',
                        dataIndex: 'balance',
                        key: 'balance',
                        align: 'right',
                        render: (val, record) => {
                            if (record.rowType === 'sectionHeader') return '';
                            return val !== null && val !== undefined ? formatKES(val) : '';
                        }
                    }
                ];

            case 'trial-balance':
                return [
                    {
                        title: 'Account',
                        dataIndex: 'account',
                        key: 'account',
                        render: (text, record) => {
                            let style = {};
                            if (record.isCategory) style = { fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '14px' };
                            if (record.isSubtotal) style = { fontWeight: 'bold', borderTop: '1px solid #ddd', backgroundColor: '#fafafa' };
                            if (record.isTotal) style = { fontWeight: 'bold', borderTop: '2px solid #000', backgroundColor: '#f0f0f0' };
                            return <span style={{ paddingLeft: text?.startsWith('  ') ? 20 : 0, ...style }}>{text}</span>;
                        }
                    },
                    {
                        title: 'Net Debit (KES)',
                        dataIndex: 'netDebit',
                        key: 'debit',
                        render: (val, record) => {
                            if (record.isCategory) return '';
                            return val !== '' ? formatKES(val) : '';
                        },
                        align: 'right'
                    },
                    {
                        title: 'Net Credit (KES)',
                        dataIndex: 'netCredit',
                        key: 'credit',
                        render: (val, record) => {
                            if (record.isCategory) return '';
                            return val !== '' ? formatKES(val) : '';
                        },
                        align: 'right'
                    }
                ];

            default:
                return [];
        }
    };

    const columns = getColumns();

    return (
        <>
            <Card title="Filters">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Date Range">
                            <RangePicker
                                onChange={(dates) => {
                                    if (!dates) return;
                                    setFilters((prev) => ({
                                        ...prev,
                                        startDate: dates[0].format('YYYY-MM-DD'),
                                        endDate: dates[1].format('YYYY-MM-DD')
                                    }));
                                }}
                                presets={[
                                    { label: 'Today', value: [dayjs(), dayjs()] },
                                    { label: 'This Week', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
                                    { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                    {
                                        label: 'Last Month',
                                        value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
                                    },
                                    {
                                        label: 'Last 6 Months',
                                        value: [dayjs().subtract(6, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
                                    },
                                    { label: 'This Year', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
                                    {
                                        label: 'Last Year',
                                        value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')]
                                    }
                                ]}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Account">
                            <Select
                                showSearch
                                allowClear
                                placeholder="Select account"
                                optionFilterProp="label"
                                filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                                options={accounts.map((acc) => ({
                                    label: `${acc.gl_code} - ${acc.account_name}`,
                                    value: acc.gl_code
                                }))}
                                value={filters.account || null}
                                onChange={(val) => setFilters((prev) => ({ ...prev, account: val }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
                                Apply Filters
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                                Clear
                            </Button>
                            <Button
                                icon={<FileExcelOutlined />}
                                onClick={exportToCSV}
                                disabled={filteredData.length === 0}
                                loading={exporting}
                            >
                                CSV
                            </Button>
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={exportToPDF}
                                disabled={filteredData.length === 0}
                                loading={exporting}
                            >
                                PDF
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card style={{ marginTop: 24 }} title={reportType?.replace(/-/g, ' ').toUpperCase()}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Title level={5}>{filters.farmName || 'SFARMS'}</Title>
                    <Title level={5} style={{ textTransform: 'capitalize' }}>
                        {reportType?.toUpperCase().replace(/-/g, ' ')}
                    </Title>
                    <Text type="secondary">
                        From: {filters.startDate ? dayjs(filters.startDate).format('DD/MM/YYYY') : 'N/A'} To:{' '}
                        {filters.endDate ? dayjs(filters.endDate).format('DD/MM/YYYY') : 'N/A'}
                    </Text>
                    <br />
                    <Text type="secondary">Basis: {reportBasis === 'cash' ? 'Cash Basis' : 'Accrual Basis'}</Text>
                </div>
                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        rowKey={(_, idx) => idx}
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSize: 500
                        }}
                        scroll={{ x: 'max-content' }}
                        bordered
                        size="small"
                    />
                </Spin>
            </Card>
        </>
    );
};

export default AccountingReports;
